// Зөвлөгөө (сурагч): уулзалт захиалах — 5 алхам: Зөвлөх → Цаг → Хэлбэр → Сэдэв → Батлах.
// Захиалга = зөвлөхийн нээлттэй цаг → "requested"; зөвлөх баталгаажуулна (зөвлөхөд имэйл очно).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input, Spin } from "antd";
import { FiArrowLeft, FiCalendar, FiCheckCircle, FiChevronRight, FiMapPin, FiUsers, FiVideo } from "react-icons/fi";
import {
  api, Avatar, Empty, dayKey, fullName, goldBtn, hm, longDate, md, outlineBtn, purpleBtn, timeRange, wdShort, TOPICS,
} from "./common";

const STEPS = ["Зөвлөх", "Цаг", "Хэлбэр", "Сэдэв", "Батлах"];
const HINTS = ["Хэнтэй уулзахаа сонго.", "Тохирох өдөр, цагаа сонго.", "Онлайн эсвэл биечлэн уулзах уу?", "Юуны талаар ярилцмаар байгаагаа товч бич.", "Мэдээллээ шалгаад илгээ."];

const pickCls = (on) =>
  `text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5C1090] ${
    on ? "border-2 border-[#5C1090] bg-[#F3EEFF]" : "border-2 border-[#E5D8F5] bg-white hover:border-[#C9B6E4]"
  }`;

function StepBar({ step, done }) {
  return (
    <ol aria-label="Алхам" className="list-none m-0 p-0 grid grid-cols-5 gap-1.5">
      {STEPS.map((l, i) => {
        const n = i + 1;
        const active = n === step && !done;
        const past = n < step || done;
        return (
          <li key={l} aria-current={active ? "step" : undefined}
            className={`text-[11px] sm:text-xs px-1 py-2 rounded-[10px] text-center leading-tight ${
              active ? "bg-[#5C1090] text-white" : past ? "bg-[#F3EEFF] text-[#5C1090]" : "bg-white text-[#6E5E8A] border border-[#E5D8F5]"
            }`}>
            <span className="font-extrabold">{n}</span> <span className={active ? "" : "hidden sm:inline"}>{l}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function CounselingBook() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [step, setStep] = useState(1);
  const [cid, setCid] = useState(null);
  const [day, setDay] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [type, setType] = useState(null);
  const [topicChip, setTopicChip] = useState(null);
  const [topic, setTopic] = useState("");
  const [request, setRequest] = useState("");
  const [saving, setSaving] = useState(false);
  const [booked, setBooked] = useState(null);

  const load = useCallback(() => api("get", "/counselors").then(setList).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);

  const c = useMemo(() => (list || []).find((x) => x.id === cid), [list, cid]);
  const days = useMemo(() => {
    if (!c) return [];
    const m = new Map();
    c.slots.forEach((s) => {
      const k = dayKey(s.startTime);
      if (!m.has(k)) m.set(k, { key: k, first: s.startTime, free: 0, slots: [] });
      const d = m.get(k);
      d.slots.push(s);
      if (!s.taken) d.free += 1;
    });
    return [...m.values()];
  }, [c]);
  const curDay = days.find((d) => d.key === day) || null;
  const slot = c?.slots.find((s) => s.id === slotId) || null;
  const finalTopic = topicChip && topicChip !== "Бусад" ? (topic.trim() ? `${topicChip}: ${topic.trim()}` : topicChip) : topic.trim();

  const pickCounselor = (x) => {
    setCid(x.id);
    setSlotId(null);
    const first = x.slots.find((s) => !s.taken);
    setDay(first ? dayKey(first.startTime) : null);
  };

  const valid = [!!c, !!slot && !slot.taken, !!type, finalTopic.length > 0 && finalTopic.length <= 200, true];
  const next = () => { if (valid[step - 1]) setStep(step + 1); };

  const submit = async () => {
    setSaving(true);
    try {
      const d = await api("post", `/book/${slotId}`, { type, topic: finalTopic.slice(0, 200), request: request.trim() || null });
      setBooked(d.id);
    } catch (e) {
      // 409 — цаг аль хэдийн захиалагдсан: жагсаалтыг шинэчилж цагаа дахин сонгуулна (алдааг interceptor харуулна)
      if (e?.response?.status === 409 || e?.response?.status === 404) {
        await load();
        setSlotId(null);
        setStep(2);
      }
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = type === "online" ? "Онлайн (Google Meet)" : type === "in_person" ? `Биечлэн${c?.room ? ` · ${c.room}` : ""}` : "";
  const whenLabel = slot ? `${longDate(slot.startTime)}, ${timeRange(slot.startTime, slot.durationMin)} · ${slot.durationMin || 60} мин` : "";

  return (
    <div className="pt-4 md:pt-6 pb-8 max-w-[680px] mx-auto flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <nav aria-label="Байршил" className="text-[13px] text-[#6E5E8A] flex items-center gap-1.5">
          <Link to="/counseling" className="text-[#5C1090] hover:text-[#3d1b6e] inline-flex items-center gap-1"><FiArrowLeft size={14} /> Зөвлөгөө</Link>
          <span aria-hidden="true">/</span><span className="text-gray-900 font-semibold">Захиалах</span>
        </nav>
        <h1 className="tds-h1">Уулзалт захиалах.</h1>
      </div>

      <div className="tds-card flex flex-col">
        <div className="px-4 md:px-6 pt-5 pb-1 flex flex-col gap-1">
          <p className="m-0 text-sm text-[#6E5E8A]">{booked ? "Бэлэн боллоо." : HINTS[step - 1]}</p>
        </div>
        <div className="px-4 md:px-6 py-4 flex flex-col gap-5">
          <StepBar step={step} done={!!booked} />

          {!list ? <div className="flex justify-center py-10"><Spin /></div> : booked ? (
            <div role="status" className="rounded-xl bg-[#F3EEFF] p-5 flex flex-col gap-2.5">
              <span className="h-11 w-11 rounded-full bg-[#5C1090] text-white flex items-center justify-center"><FiCheckCircle size={22} /></span>
              <strong className="text-lg font-extrabold text-[#5C1090]">Хүсэлт илгээгдлээ.</strong>
              <span className="text-sm leading-relaxed text-gray-800">
                {fullName(c)} · {whenLabel} · {typeLabel}. Зөвлөх баталгаажуулмагц танд мэдэгдэл ирнэ. Тэр болтол уулзалт “Хүлээгдэж буй” төлөвтэй байна.
              </span>
              <div className="flex flex-wrap gap-2 mt-1">
                <Link to={`/counseling/${booked}`} className={purpleBtn}>Уулзалтыг харах <FiChevronRight size={16} /></Link>
                <Link to="/counseling" className={outlineBtn}>Миний зөвлөгөө</Link>
              </div>
            </div>
          ) : (
            <>
              {step === 1 && (
                list.length === 0 ? (
                  <Empty icon={FiUsers} title="Зөвлөх бүртгэгдээгүй байна" text="Сургуулийн зөвлөхүүд системд нэмэгдмэгц энд харагдана." />
                ) : (
                  <div role="radiogroup" aria-label="Зөвлөх сонгох" className="flex flex-col gap-2.5">
                    {list.map((x) => {
                      const free = x.slots.filter((s) => !s.taken).length;
                      const on = cid === x.id;
                      return (
                        <button key={x.id} type="button" role="radio" aria-checked={on} disabled={!free} onClick={() => pickCounselor(x)}
                          className={`${pickCls(on)} w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-[#E5D8F5]`}>
                          <Avatar person={x} size={42} dark />
                          <span className="flex flex-col gap-0.5 flex-1 min-w-0">
                            <span className="text-[15px] font-bold text-gray-900">
                              {fullName(x)}{x.title && <span className="font-medium text-[#6E5E8A]"> · {x.title}</span>}
                            </span>
                            <span className="text-[13px] text-[#6E5E8A] leading-snug">{x.focus || (x.room ? `Өрөө: ${x.room}` : "Сургуулийн зөвлөх")}</span>
                          </span>
                          <span className={`text-xs font-bold shrink-0 ${free ? "text-[#5C1090]" : "text-[#6E5E8A]"}`}>{free ? `${free} сул цаг` : "Сул цаг алга"}</span>
                        </button>
                      );
                    })}
                    {list.every((x) => !x.slots.some((s) => !s.taken)) && (
                      <p className="m-0 text-[13px] text-[#6E5E8A] leading-relaxed">
                        Ирэх 4 долоо хоногт сул цаг алга байна. Зөвлөхүүд шинэ цаг нээмэгц энд гарна — дараа дахин шалгаарай.
                      </p>
                    )}
                  </div>
                )
              )}

              {step === 2 && c && (
                <div className="flex flex-col gap-3.5">
                  <span className="text-sm font-medium">{fullName(c)}-ийн сул цаг</span>
                  {days.length === 0 ? (
                    <Empty icon={FiCalendar} title="Сул цаг алга" text="Өөр зөвлөх сонгох эсвэл дараа дахин шалгаарай." />
                  ) : (
                    <>
                      <div role="radiogroup" aria-label="Өдөр" className="hscroll !gap-2">
                        {days.map((d) => (
                          <button key={d.key} type="button" role="radio" aria-checked={day === d.key} onClick={() => { setDay(d.key); setSlotId(null); }}
                            className={`${pickCls(day === d.key)} w-[72px] shrink-0 flex flex-col items-center gap-0.5 py-2.5 rounded-xl ${d.free ? "" : "opacity-60"}`}>
                            <span className="text-xs font-semibold text-[#6E5E8A]">{wdShort(d.first)}</span>
                            <span className="text-base font-extrabold text-gray-900">{md(d.first)}</span>
                          </button>
                        ))}
                      </div>
                      {curDay ? (
                        <div role="radiogroup" aria-label="Цаг" className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {curDay.slots.map((s) => {
                            const on = slotId === s.id;
                            return (
                              <button key={s.id} type="button" role="radio" aria-checked={on} disabled={s.taken} onClick={() => setSlotId(s.id)}
                                aria-label={`${hm(s.startTime)}${s.taken ? " — захиалагдсан" : ""}`}
                                className={`h-11 rounded-[10px] text-sm font-bold text-center tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5C1090] ${
                                  s.taken
                                    ? "border border-[#E5D8F5] bg-[#F3EEFF] text-[#6E5E8A] line-through cursor-not-allowed"
                                    : on
                                    ? "border-2 border-[#5C1090] bg-[#5C1090] text-white"
                                    : "border-2 border-[#E5D8F5] bg-white text-gray-900 hover:border-[#C9B6E4]"
                                }`}>
                                {hm(s.startTime)}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-sm text-[#6E5E8A]">Өдрөө сонгоно уу.</span>
                      )}
                      <span className="text-xs text-[#6E5E8A]">
                        {slot ? `Сонгосон: ${longDate(slot.startTime)}, ${timeRange(slot.startTime, slot.durationMin)} · ${slot.durationMin || 60} мин. ` : ""}
                        Саарал цаг аль хэдийн захиалагдсан.
                      </span>
                    </>
                  )}
                </div>
              )}

              {step === 3 && (
                <div role="radiogroup" aria-label="Уулзалтын хэлбэр" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: "online", label: "Онлайн", icon: FiVideo, desc: "Google Meet-ээр уулзана. Холбоос энэ хуудсанд гарна." },
                    { id: "in_person", label: "Биечлэн", icon: FiMapPin, desc: c?.room ? `Зөвлөхийн өрөө: ${c.room}.` : "Зөвлөхийн өрөөнд биечлэн уулзана." },
                  ].map((t) => (
                    <button key={t.id} type="button" role="radio" aria-checked={type === t.id} onClick={() => setType(t.id)}
                      className={`${pickCls(type === t.id)} flex flex-col items-start gap-2 p-4 rounded-xl`}>
                      <span className="h-10 w-10 rounded-[10px] bg-[#5C1090] text-white inline-flex items-center justify-center"><t.icon size={20} /></span>
                      <span className="text-base font-bold text-gray-900">{t.label}</span>
                      <span className="text-[13px] text-[#6E5E8A] leading-relaxed">{t.desc}</span>
                    </button>
                  ))}
                </div>
              )}

              {step === 4 && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-semibold text-gray-900">Сэдэв <span className="text-red-500">*</span></span>
                    <div role="radiogroup" aria-label="Санал болгох сэдэв" className="flex flex-wrap gap-2">
                      {TOPICS.map((t) => (
                        <button key={t} type="button" role="radio" aria-checked={topicChip === t} onClick={() => setTopicChip(topicChip === t ? null : t)}
                          className={`pchip !h-8 !text-[13px] ${topicChip === t ? "pchip-on" : "pchip-off"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                    <Input id="b-topic" value={topic} onChange={(e) => setTopic(e.target.value)} maxLength={200}
                      placeholder={topicChip && topicChip !== "Бусад" ? "Нарийвчилбал (заавал биш): ж: Эссэний ноорог хянах" : "Жишээ нь: Эссэний ноорог хянах"} />
                  </div>
                  <label className="flex flex-col gap-1.5" htmlFor="b-desc">
                    <span className="text-sm font-semibold text-gray-900">Юуны талаар ярилцмаар байна вэ?</span>
                    <span className="text-xs text-[#6E5E8A]">Зөвлөх тань уулзалтын өмнө уншиж бэлтгэнэ.</span>
                    <Input.TextArea id="b-desc" value={request} onChange={(e) => setRequest(e.target.value)} autoSize={{ minRows: 4, maxRows: 10 }} maxLength={5000}
                      placeholder="Юу гацаж байна, юунд тусламж хэрэгтэй байна вэ?" />
                  </label>
                </div>
              )}

              {step === 5 && (
                <dl className="m-0 border border-[#E5D8F5] rounded-xl bg-[#FAF7FF] px-4 py-4 sm:px-5 grid grid-cols-[88px_minmax(0,1fr)] sm:grid-cols-[120px_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                  <dt className="text-[#6E5E8A]">Зөвлөх</dt><dd className="m-0 font-bold">{fullName(c)}{c?.title ? ` · ${c.title}` : ""}</dd>
                  <dt className="text-[#6E5E8A]">Цаг</dt><dd className="m-0 font-bold">{whenLabel}</dd>
                  <dt className="text-[#6E5E8A]">Хэлбэр</dt><dd className="m-0 font-bold">{typeLabel}</dd>
                  <dt className="text-[#6E5E8A]">Сэдэв</dt><dd className="m-0 font-bold break-words">{finalTopic}</dd>
                  <dt className="text-[#6E5E8A]">Тайлбар</dt><dd className="m-0 leading-relaxed whitespace-pre-wrap break-words">{request.trim() || "—"}</dd>
                </dl>
              )}
            </>
          )}
        </div>

        {list && !booked && (
          <div className="px-4 md:px-6 py-4 border-t border-[#F3EEFF] flex items-center gap-2">
            <button type="button" onClick={() => navigate("/counseling")} className="h-10 px-3 rounded-[10px] text-sm font-medium text-gray-700 hover:bg-[#FAF7FF] mr-auto">Болих</button>
            {step > 1 && <button type="button" onClick={() => setStep(step - 1)} className={outlineBtn}>Буцах</button>}
            {step < 5 ? (
              <button type="button" onClick={next} disabled={!valid[step - 1]} className={purpleBtn}>Үргэлжлүүлэх</button>
            ) : (
              <button type="button" onClick={submit} disabled={saving} className={`${goldBtn} !h-10 !text-sm`}>
                {saving ? <Spin size="small" /> : null} Захиалга илгээх
              </button>
            )}
          </div>
        )}
      </div>
      {!booked && <p className="m-0 text-xs text-[#6E5E8A] text-center px-4">Захиалга илгээсний дараа зөвлөх баталгаажуулна. Хүсэлтээ “Миний зөвлөгөө” хэсгээс цуцалж болно.</p>}
    </div>
  );
}
